/**
 * Shared client-side pagination walk for the TLC list APIs (tcx-core-platforms-903 §8 F7).
 *
 * Every list page (Launches, Groups, Active Packages) and the group modals page through a
 * `nextToken`-based API the same way: loop until there's no `nextToken`, cap the walk at `MAX_PAGES`,
 * bail out on the first non-200 page, and treat a leftover `nextToken` at the cap as a truncated
 * (incomplete) result rather than a complete list. That ~20-line loop was copy-pasted across six call
 * sites, and the "leftover-nextToken-at-cap means truncated" fix had to be applied to each copy
 * separately across revisions — exactly the drift duplication invites. Centralizing it here means a
 * new list page gets the correct behavior for free and the fix lives in one place
 * (finding on CR-301380769).
 */

// Safety cap on pagination loops (each page is up to PAGE_SIZE rows).
export const MAX_PAGES = 50;
export const PAGE_SIZE = 1000;

/** One page of a `nextToken`-based list response, normalized for {@link paginateAll}. */
export interface PaginatedPage<T> {
  /** The items on this page (already defaulted to [] by the caller). */
  items: T[];
  /** The continuation token for the next page, or undefined/empty when this is the last page. */
  nextToken?: string;
  /** The page's HTTP status code (0 when absent). */
  statusCode: number;
}

/** Outcome of a full pagination walk. */
export interface PaginateResult<T> {
  /** Every item collected across the successful pages walked. */
  items: T[];
  /** True only if every page walked returned HTTP 200. */
  ok: boolean;
  /**
   * True when the walk stopped at MAX_PAGES with a `nextToken` still outstanding — i.e. more pages
   * remain than the cap allows, so `items` is a partial list. Only meaningful when `ok` is true.
   */
  truncated: boolean;
}

/**
 * Walk a `nextToken`-based list API to completion (up to MAX_PAGES), collecting every item.
 *
 * @param fetchPage called once per page with the current `nextToken` (undefined on the first call);
 *                  must resolve to the page's items, its `nextToken`, and its HTTP status code.
 * @param maxPages  safety cap on the number of pages to walk (defaults to {@link MAX_PAGES}).
 * @returns `{ items, ok, truncated }`. Callers should commit `items` only when `ok && !truncated`,
 *          and otherwise surface an error (non-200 page when `!ok`; too many pages when `truncated`).
 */
export async function paginateAll<T>(
  fetchPage: (nextToken?: string) => Promise<PaginatedPage<T>>,
  maxPages: number = MAX_PAGES
): Promise<PaginateResult<T>> {
  const items: T[] = [];
  let nextToken: string | undefined = undefined;
  let pages = 0;
  let ok = true;
  do {
    const page = await fetchPage(nextToken);
    if (page.statusCode !== 200) {
      ok = false;
      break;
    }
    items.push(...page.items);
    nextToken = page.nextToken;
    pages += 1;
  } while (nextToken && pages < maxPages);

  return { items, ok, truncated: ok && Boolean(nextToken) };
}
